import { IGetCashUserStatusRes } from "src/service/types/pay.type";
import { IGetUnregisterBillRes } from "src/service/types/pay.type";
import { IGetMyServiceListRes } from "src/service/types/console.type";
import { ProductKey } from "src/config/config";

// 콘솔 초기화(useCheckConsole)에서 사용하는 순수 판정 로직 모음.
// 라우팅/상태 변경 등 부수효과 없이 입력 → 결과만 계산한다.
// (효과 적용은 useCheckConsole가 이 결과를 받아 수행 — 순수/효과 분리)

// 현금결제 대기 상태 여부
// - 현금(CASH) 결제 서비스가 입금 대기(WAIT)
// - 또는 신규 신청(serviceUid 없음)이 입금 신청(APPLY) 상태
export function isCashWaiting(
  cashStatus: IGetCashUserStatusRes | undefined,
  serviceList: IGetMyServiceListRes[]
): boolean {
  return (
    (serviceList[0]?.payCompany === "CASH" && cashStatus?.status === "WAIT") ||
    (cashStatus?.serviceUid === "" && cashStatus?.status === "APPLY")
  );
}

// (OCI + WRAPSODY_ECO) 또는 서비스 미보유 → 미생성 청구/프로모션 분기로 진입할지 여부
export function shouldEnterUnregisterFlow(
  isOciProduct: boolean,
  productKey: ProductKey,
  serviceList: IGetMyServiceListRes[]
): boolean {
  return (
    (isOciProduct && productKey === "WRAPSODY_ECO") ||
    serviceList?.length === 0
  );
}

// 미생성 청구(결제됐으나 사이트 미생성) 상태 분류
// - PAY / PROMOTION: 해당 청구가 남아 사이트 생성 필요
// - NONE: 두 목록 모두 빈 배열(로드 완료, 남은 청구 없음) → 프로모션 코드 체크 진행
// - PENDING: 아직 로드되지 않음(undefined) → 아무 동작도 하지 않음(기존 동작 보존)
export type UnregisterKind = "PAY" | "PROMOTION" | "NONE" | "PENDING";
export function classifyUnregister(
  unregisterPay: IGetUnregisterBillRes[] | undefined,
  unregisterPromotion: IGetUnregisterBillRes[] | undefined
): UnregisterKind {
  // undefined는 -1로 취급하여 ">0"·"===0" 어느 쪽에도 걸리지 않게 한다(PENDING).
  const payLen = unregisterPay?.length ?? -1;
  const promotionLen = unregisterPromotion?.length ?? -1;
  if (payLen > 0) return "PAY";
  if (promotionLen > 0) return "PROMOTION";
  if (payLen === 0 && promotionLen === 0) return "NONE";
  return "PENDING";
}

// 프로모션 코드 체크 결과가 없을 때(NONE)의 이동 대상 판정
// - OCI: 서비스 보유 여부에 따라 콘솔/요금제 선택
// - 그 외: 프로모션 페이지
export type PromotionRouteTarget = "CONSOLE" | "SITE_TYPE" | "SITE_PROMOTION";
export function resolvePromotionRoute(
  isOciProduct: boolean,
  hasService: boolean
): PromotionRouteTarget {
  if (isOciProduct) return hasService ? "CONSOLE" : "SITE_TYPE";
  return "SITE_PROMOTION";
}

// 프로모션 체크 결과 → navStep 매핑 (해당 없으면 null → navStep 미변경)
export function navStepFromPromotion(
  result: string | undefined | null
): number | null {
  if (result === "T_APPROVED") return 3;
  if (result === "T_APPLY" || result === "C_APPLY") return 2;
  return null;
}

// 서비스 보유 시 사이트 생성 프로그레스 폴링 대상 여부
// (AWS 계열만 폴링: OCI/DSP/DSPM 제외, 이미 SERVICE 상태면 제외)
export function shouldPollProvisioning(params: {
  serviceStatus: string;
  isOciProduct: boolean;
  isDspProduct: boolean;
  isDspmProduct: boolean;
}): boolean {
  const { serviceStatus, isOciProduct, isDspProduct, isDspmProduct } = params;
  return (
    serviceStatus !== "SERVICE" &&
    !isOciProduct &&
    !isDspProduct &&
    !isDspmProduct
  );
}
